/**
 * Help Scene: displays game instructions, lore, and visual reference.
 *
 * Organized in a logical flow: lore → game modes → basic mechanics →
 * hazards → collectibles → advanced mechanics → shop → controls → tips.
 * Uses procedural texture icons inline so the player can visually
 * identify items before entering the maze.
 *
 * Navigable with UP/DOWN arrows (scrollable) and ESC/ENTER to return.
 */

import Phaser from 'phaser';
import {
  SCENE_HELP,
  SCENE_TITLE,
  TEX_PLAYER,
  TEX_FOOD_FRUIT,
  TEX_FOOD_CHICKEN,
  TEX_FOOD_TUNA,
  TEX_COIN,
  TEX_WATER,
  TEX_LAVA,
  TEX_POISON,
  TEX_SPIKE,
  TEX_EXIT,
  TEX_LIFE_ICON,
  TEX_WALL,
  TEX_KEY_METALLIC,
  TEX_KEY_COPPER,
  TEX_KEY_GOLD,
  TEX_CHEST_METALLIC,
  TEX_CHEST_COPPER,
  TEX_CHEST_GOLD,
  TEX_LOOT_HEART,
  TEX_LOOT_STAR,
  TEX_LOOT_AGMUTH,
  TEX_LOOT_COIN_BAG,
  TEX_COMPASS,
  TEX_THREIA_TOTEM,
  TEX_HAMMER_OF_THREIA,
  TEX_MANUSCRIPT,
  TEX_BAT_WINGS_UP,
  TEX_SHOP_THREIAS_GUIDE,
  TEX_SHOP_HEART_POTION,
  TEX_SHOP_TOTEM_VORACIOUS,
  TEX_SHOP_AGMUTH_PENDANT,
  TEX_SHOP_LAVA_RING,
  TEX_SHOP_POISON_RING,
  TEX_SHOP_WATER_RING,
  TEX_SHOP_KEY_PENDANT,
  TEX_SHOP_HEART_PENDANT,
  TEX_SHOP_ELEMENTAL_RING,
  TEX_SHOP_HAMMER_OF_THREIA,
  TEX_SHOP_TEAR_OF_THREIA,
  TEX_GOLDEN_EXIT,
} from '../utils/constants.js';
import {
  GAME_WIDTH,
  GAME_HEIGHT,
  COLOR_WHITE,
  BORDER_MARGIN,
  FOOD_HEAL_AMOUNT,
  FOOD_HEAL_FRUIT,
  FOOD_HEAL_TUNA,
  FOOD_HEAL_CHICKEN,
  HEALTH_DRAIN_PER_MOVE,
  WATER_DRAIN_MULTIPLIER,
  WATER_IDLE_DRAIN_AMOUNT,
  WATER_IDLE_DRAIN_INTERVAL,
  LAVA_DRAIN_MULTIPLIER,
  LAVA_IDLE_DRAIN_AMOUNT,
  LAVA_IDLE_DRAIN_INTERVAL,
  COINS_PER_EXTRA_LIFE,
  POISON_DURATION,
  POISON_DRAIN_AMOUNT,
  POISON_DRAIN_INTERVAL,
  INITIAL_LIVES,
  MAX_HEALTH,
  TILE_SIZE,
  SPIKE_WOUND_DURATION,
  SPIKE_DRAIN_AMOUNT,
  SPIKE_DRAIN_INTERVAL,
  KEY_CHEST_MIN_LEVEL,
  MAX_PLAYER_KEYS,
  AGMUTH_MAX_PER_GAME,
  COMPASS_SPAWN_CHANCE,
  MANUSCRIPT_SPAWN_CHANCE,
  AUDIO_MENU_CLICK,
  AUDIO_ESC_PRESSED,
  SHOP_PRICE_THREIAS_GUIDE,
  SHOP_PRICE_HEART_POTION,
  SHOP_PRICE_TOTEM_VORACIOUS,
  SHOP_PRICE_AGMUTH_PENDANT,
  SHOP_PRICE_LAVA_RING,
  SHOP_PRICE_POISON_RING,
  SHOP_PRICE_WATER_RING,
  SHOP_PRICE_KEY_PENDANT,
  SHOP_PRICE_HEART_PENDANT,
  SHOP_PRICE_ELEMENTAL_RING,
  SHOP_PRICE_HAMMER_OF_THREIA,
  SHOP_PRICE_TEAR_OF_THREIA,
  HEART_PENDANT_DRAIN_REDUCTION,
  THREIAS_GUIDE_REVEAL_CHANCE,
  TOTEM_VORACIOUS_ATTRACT_RADIUS,
  AGMUTH_PENDANT_BONUS,
  KEY_PENDANT_SPAWN_BONUS,
  DURABILITY_THREIAS_GUIDE,
  DURABILITY_WATER_RING,
  DURABILITY_LAVA_RING,
  DURABILITY_POISON_RING,
  DURABILITY_ELEMENTAL_RING,
  DURABILITY_HAMMER_OF_THREIA,
  DURABILITY_TOTEM_VORACIOUS,
  DURABILITY_HEART_PENDANT,
  BAT_MIN_LEVEL,
  BAT_ATTACK_DAMAGE_MIN,
  BAT_ATTACK_DAMAGE_MAX,
  CHEST_COIN_BAG_AMOUNT,
  HAMMER_BREAK_PRESSES,
  TREASURE_ROOM_LEVEL_INTERVAL,
  TREASURE_ROOM_SPAWN_CHANCE,
  TREASURE_ROOM_TIMER_SECONDS,
  TREASURE_ROOM_COIN_COUNT,
  TREASURE_ROOM_COIN_BAG_COUNT,
  EXPLORER_FREE_CONTINUES,
  EXPLORER_SCORE_PENALTY,
  EXPLORER_COIN_PENALTY,
  EXPLORER_MIN_COIN_COST,
  SHOP_PRICE_KEY_METALLIC,
  SHOP_PRICE_KEY_COPPER,
  SHOP_PRICE_KEY_GOLD,
  SCORE_TO_COINS_RATE,
} from '../config/game-config.js';
import { getSfxVol } from '../utils/audio-config.js';

// --- Text styles ---

const SECTION_TITLE_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '16px',
  color: '#ffffff',
};

const BODY_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#cccccc',
  wordWrap: { width: 540 },
  lineSpacing: 5,
};

const ITEM_LABEL_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '12px',
  color: '#ffffff',
};

const ITEM_DESC_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#aaaaaa',
  wordWrap: { width: 430 },
  lineSpacing: 3,
};

const HINT_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '11px',
  color: '#777777',
};

const PAGE_HEADER_STYLE = {
  fontFamily: '"Courier New", Courier, monospace',
  fontSize: '22px',
  color: '#ffffff',
  letterSpacing: 3,
};

export default class HelpScene extends Phaser.Scene {
  constructor() {
    super({ key: SCENE_HELP });
  }

  create() {
    this.cameras.main.setBackgroundColor('#000000');
    this.cameras.main.fadeIn(400, 0, 0, 0);

    this._transitioning = false;

    // Section anchors — stores Y positions for TOC navigation
    this._sectionAnchors = {};

    // Decorative border (consistent with all scenes)
    this._drawBorder();

    // Content area bounds
    const contentLeft = BORDER_MARGIN + 30;
    const contentRight = GAME_WIDTH - BORDER_MARGIN - 30;
    const contentWidth = contentRight - contentLeft;
    const cx = GAME_WIDTH / 2;

    // -- Scrollable container --
    // We place all content in a container and scroll it with UP/DOWN.
    // A graphics mask clips content to the area inside the decorative border.
    this._scrollContainer = this.add.container(0, 0);
    this._scrollY = 0;

    const maskPad = BORDER_MARGIN + 8;
    const maskShape = this.make.graphics({ add: false });
    maskShape.fillStyle(0xffffff);
    maskShape.fillRect(maskPad, maskPad,
      GAME_WIDTH - maskPad * 2, GAME_HEIGHT - maskPad * 2);
    this._scrollContainer.setMask(maskShape.createGeometryMask());

    // Content is built at absolute positions and added to the container.
    let y = 45;

    // ============================================================
    // HEADER
    // ============================================================
    const header = this.add.text(cx, y, 'HELP', PAGE_HEADER_STYLE).setOrigin(0.5);
    this._scrollContainer.add(header);
    y += 35;

    // Decorative line
    const lineG = this.add.graphics();
    lineG.lineStyle(1, COLOR_WHITE, 0.3);
    lineG.lineBetween(cx - 120, y, cx + 120, y);
    this._scrollContainer.add(lineG);
    y += 20;

    // ============================================================
    // LORE / OBJECTIVE
    // ============================================================
    y = this._addSection(contentLeft, y, 'THE LOST SOUL',
      'A restless soul, still bound to the earthly plane, ' +
      'wanders the endless labyrinth of Limbo. Guide this lost spirit ' +
      'through the shifting maze, feed it to sustain its fading energy, ' +
      'and help it find the exit before darkness consumes it.\n\n' +
      'Each level the labyrinth grows larger and more treacherous. ' +
      'Water puddles and rivers of lava block the path. ' +
      'Find the exit to advance deeper into Limbo.');
    y += 8;

    // ============================================================
    // TABLE OF CONTENTS (clickable)
    // ============================================================
    /** @type {number} Scroll threshold beyond which the "back to top" hint appears. */
    this._tocEndY = 0;
    {
      const tocTitle = this.add.text(contentLeft, y, '-- CONTENTS --', {
        ...SECTION_TITLE_STYLE,
        color: '#999999',
      });
      this._scrollContainer.add(tocTitle);
      y += tocTitle.height + 6;

      // Two-column TOC layout with clickable entries
      const tocEntries = [
        // [label, anchor key] — left column, then right column
        ['Game Modes', 'GAME MODES'],
        ['Floor Hazards', 'FLOOR HAZARDS'],
        ['Controls', 'CONTROLS'],
        ['Keys & Chests', 'KEYS & CHESTS'],
        ['Your Soul', 'YOUR SOUL'],
        ['The Mystic Compass', 'THE MYSTIC COMPASS'],
        ['The Exit', 'THE EXIT'],
        ['Manuscripts', 'MANUSCRIPTS'],
        ['Food', 'FOOD'],
        ['Bats', 'BATS'],
        ['Coins', 'COINS'],
        ['Agmuth — Ghost Mode', 'AGMUTH — GHOST MODE'],
        ['Fog of War', 'FOG OF WAR'],
        ["Threia's Blessing", "THREIA'S BLESSING"],
        ['Resting', 'RESTING'],
        ['Hammer of Threia', 'HAMMER OF THREIA'],
        ["Vorath's Vault", "VORATH'S VAULT"],
        ['The Shop', 'THE SHOP'],
        ['Level Events', 'LEVEL EVENTS'],
        ['Secret Passages', 'SECRET PASSAGES'],
        ['Shadow Walkers', 'SHADOW WALKERS'],
        ['Moving Walls', 'MOVING WALLS'],
        ['Bat Nests', 'BAT NESTS'],
        ['Mini-map', 'MINI-MAP'],
        ['Direction Trail', 'DIRECTION TRAIL'],
        ['Tips', 'TIPS'],
      ];

      const colWidth = 230;
      const rowH = 16;
      const rows = tocEntries.length / 2;
      const tocStyle = {
        fontFamily: '"Courier New", Courier, monospace',
        fontSize: '11px',
        color: '#888888',
      };
      const tocHoverColor = '#ffffff';
      const tocNormalColor = '#888888';

      for (let i = 0; i < tocEntries.length; i++) {
        const col = i % 2;
        const row = Math.floor(i / 2);
        const ex = contentLeft + 10 + col * colWidth;
        const ey = y + row * rowH;
        const [label, anchor] = tocEntries[i];

        const t = this.add.text(ex, ey, `· ${label}`, tocStyle)
          .setInteractive({ useHandCursor: true });
        this._scrollContainer.add(t);

        t.on('pointerover', () => t.setColor(tocHoverColor));
        t.on('pointerout', () => t.setColor(tocNormalColor));
        t.on('pointerdown', () => {
          try { this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.3) }); } catch (_e) { /* */ }
          this._scrollToSection(anchor);
        });
      }

      y += rows * rowH + 12;
    }
    this._tocEndY = y;
    y = this._addSeparator(y);

    // ============================================================
    // GAME MODES
    // ============================================================
    this._sectionAnchors['GAME MODES'] = y;
    y = this._addSectionTitle(contentLeft, y, 'GAME MODES');
    y += 4;
    {
      const modesIntro = this.add.text(contentLeft, y,
        'When you begin a new journey, you must choose how to face the ' +
        'labyrinth. Two paths lie before you — each demands a different ' +
        'kind of courage.',
        BODY_STYLE);
      this._scrollContainer.add(modesIntro);
      y += modesIntro.height + 10;
    }
    y = this._addSectionTitle(contentLeft, y, 'CLASSIC');
    y += 2;
    {
      const classicBody = this.add.text(contentLeft, y,
        'The labyrinth shows no mercy. When your last heart stops beating, ' +
        'your journey ends — truly and irrevocably. There is no bargaining ' +
        'with death, no second chance. Every step carries weight, every ' +
        'decision could be your last.\n\n' +
        'This is the way the labyrinth was meant to be faced: with nothing ' +
        'but skill, resourcefulness, and the desperate will to survive. ' +
        'The souls who endure Classic mode earn every level through pure ' +
        'mastery of the maze.',
        BODY_STYLE);
      this._scrollContainer.add(classicBody);
      y += classicBody.height + 10;
    }
    y = this._addSectionTitle(contentLeft, y, 'EXPLORER');
    y += 2;
    {
      const explorerBody = this.add.text(contentLeft, y,
        'Death is not the end — but it comes at a price. When all lives are ' +
        'lost, a dark whisper echoes through the corridors: the labyrinth ' +
        'itself offers you a deal. Accept, and you rise again exactly where ' +
        'you fell, fully restored, the maze unchanged around you.\n\n' +
        `The first ${EXPLORER_FREE_CONTINUES} times, the toll is light — merely ` +
        `${Math.round(EXPLORER_SCORE_PENALTY * 100)}% of your score, ` +
        'a fragment of glory sacrificed for another chance. But after that, ' +
        'the labyrinth grows greedier: it demands ' +
        `${Math.round(EXPLORER_COIN_PENALTY * 100)}% of all your coins as well, ` +
        `and will not settle for less than ${EXPLORER_MIN_COIN_COST}.\n\n` +
        'Each resurrection costs more than the last. The maze feeds on your ' +
        'wealth, and when you can no longer pay, the deal is off. ' +
        'Choose this path if you wish to explore deeper into Limbo without ' +
        'the fear of starting over — but know that every death chips away ' +
        'at what you have earned.',
        BODY_STYLE);
      this._scrollContainer.add(explorerBody);
      y += explorerBody.height + 12;
    }
    y = this._addSeparator(y);

    // ============================================================
    // CONTROLS (placed early so players learn keys before mechanics)
    // ============================================================
    this._sectionAnchors['CONTROLS'] = y;
    y = this._addSection(contentLeft, y, 'CONTROLS',
      'Arrow keys ........ Move\n' +
      'O ................. Open adjacent chest\n' +
      'A ................. Activate Agmuth (ghost mode)\n' +
      'B (rapid press) ... Break wall (Hammer of Threia)\n' +
      'H (rapid press) ... Pray to Threia\n' +
      'Z (hold) .......... Zoom out (view explored map)\n' +
      'TAB ............... Toggle mini-map\n' +
      'I ................. Game info (items & shop)\n' +
      'M ................. Toggle music\n' +
      'ESC ............... Pause / Resume\n' +
      'ENTER / SPACE ..... Confirm selection');
    y += 8;
    y = this._addSeparator(y);

    // ============================================================
    // YOUR SOUL (player icon)
    // ============================================================
    this._sectionAnchors['YOUR SOUL'] = y;
    y = this._addSectionTitle(contentLeft, y, 'YOUR SOUL');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_PLAYER, 'The Lost Soul',
      `You start with ${INITIAL_LIVES} lives and ${MAX_HEALTH} HP. Every step drains ${HEALTH_DRAIN_PER_MOVE} HP. ` +
      'If health reaches zero, you lose a life and respawn. Lose all lives and the journey ends.',
      2.0);
    y += 4;

    // ============================================================
    // THE EXIT
    // ============================================================
    this._sectionAnchors['THE EXIT'] = y;
    y = this._addIconRow(contentLeft, y, TEX_EXIT, 'The Exit',
      'A glowing portal marks the way out. Reach it to complete the level and descend deeper into the labyrinth.');
    y += 12;

    // ============================================================
    // FOOD
    // ============================================================
    this._sectionAnchors['FOOD'] = y;
    y = this._addSectionTitle(contentLeft, y, 'FOOD');
    y += 4;
    {
      const descIntro = this.add.text(contentLeft, y,
        'Food appears throughout the maze and restores HP when collected. ' +
        'Each type heals a different amount. Food also awards score points. ' +
        'It disappears after a while, so grab it quickly!',
        BODY_STYLE);
      this._scrollContainer.add(descIntro);
      y += descIntro.height + 10;
    }

    y = this._addIconRow(contentLeft, y, TEX_FOOD_FRUIT, 'Fruit',
      `Restores ${FOOD_HEAL_FRUIT} HP. A fresh fruit — the most common nourishment.`);
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_FOOD_CHICKEN, 'Chicken',
      `Restores ${FOOD_HEAL_CHICKEN} HP. A hearty drumstick — the best meal you can find.`,
      1.8, 180);
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_FOOD_TUNA, 'Tuna',
      `Restores ${FOOD_HEAL_TUNA} HP. A tin of tuna — portable and reliable.`);
    y += 12;

    // ============================================================
    // COINS
    // ============================================================
    this._sectionAnchors['COINS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'COINS');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_COIN, 'Coin',
      `Collect coins for bonus points. Every ${COINS_PER_EXTRA_LIFE} coins grant an extra life! ` +
      'Coins pulse with a soft glow and disappear after some time.\n\n' +
      `When the game ends, your final score is also converted into wallet coins ` +
      `(${SCORE_TO_COINS_RATE} score = 1 coin). Higher scores mean more coins to spend in the Shop!`);
    y += 12;

    // ============================================================
    // FOG OF WAR
    // ============================================================
    this._sectionAnchors['FOG OF WAR'] = y;
    y = this._addSection(contentLeft, y, 'FOG OF WAR',
      'The maze is shrouded in darkness. Only tiles near the soul are visible. ' +
      'Previously visited areas remain dimly lit. Explore carefully — you never ' +
      'know what lies ahead. Hold Z to zoom out and view all explored areas.');
    y += 8;
    y = this._addSeparator(y);

    // ============================================================
    // REST / HEALING
    // ============================================================
    this._sectionAnchors['RESTING'] = y;
    y = this._addSection(contentLeft, y, 'RESTING',
      'Stand still for a few seconds and the soul will rest, ' +
      'slowly recovering health. Floating "Z" letters indicate rest mode. ' +
      'But beware: resting costs score points! Moving interrupts rest immediately.');
    y += 8;

    // ============================================================
    // FLOOR TYPES
    // ============================================================
    this._sectionAnchors['FLOOR HAZARDS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'FLOOR HAZARDS');
    y += 4;
    {
      const floorIntro = this.add.text(contentLeft, y,
        'Not all paths are safe. As you descend deeper, the labyrinth ' +
        'reveals ever more treacherous ground:',
        BODY_STYLE);
      this._scrollContainer.add(floorIntro);
      y += floorIntro.height + 10;
    }

    // Normal floor (wall texture used as a small swatch, tinted dark)
    y = this._addFloorRow(contentLeft, y, null, 'Normal Floor',
      `Standard path. Drains ${HEALTH_DRAIN_PER_MOVE} HP per step. Safe to walk on.`,
      0x333333);
    y += 2;

    y = this._addFloorRow(contentLeft, y, TEX_WATER, 'Water',
      `Puddles of water drain ${HEALTH_DRAIN_PER_MOVE * WATER_DRAIN_MULTIPLIER} HP per step ` +
      `(x${WATER_DRAIN_MULTIPLIER} normal). Standing still on water also drains ` +
      `${WATER_IDLE_DRAIN_AMOUNT} HP every ${WATER_IDLE_DRAIN_INTERVAL / 1000}s. Appears from level 2.`);
    y += 2;

    y = this._addFloorRow(contentLeft, y, TEX_LAVA, 'Lava',
      `Molten lava drains ${HEALTH_DRAIN_PER_MOVE * LAVA_DRAIN_MULTIPLIER} HP per step ` +
      `(x${LAVA_DRAIN_MULTIPLIER} normal). Standing still on lava also drains ` +
      `${LAVA_IDLE_DRAIN_AMOUNT} HP every ${LAVA_IDLE_DRAIN_INTERVAL / 1000}s. Appears from level 3. Avoid!`);
    y += 2;

    y = this._addFloorRow(contentLeft, y, TEX_POISON, 'Poison',
      `Toxic ground that poisons you for ${POISON_DURATION / 1000} seconds: ` +
      `drains ${POISON_DRAIN_AMOUNT} HP every ${POISON_DRAIN_INTERVAL / 1000}s and halves your speed. ` +
      `Appears from level 4 onward. Watch for the green glow!`);
    y += 2;

    y = this._addFloorRow(contentLeft, y, TEX_SPIKE, 'Spikes',
      `Sharp floor spikes that wound you for ${SPIKE_WOUND_DURATION / 1000} seconds: ` +
      `drains ${SPIKE_DRAIN_AMOUNT} HP every ${SPIKE_DRAIN_INTERVAL / 1000}s, slows movement, ` +
      `and leaves a blood trail. Appears from level 5. Watch for the red glow!`);
    y += 12;
    y = this._addSeparator(y);

    // ============================================================
    // KEYS & CHESTS
    // ============================================================
    this._sectionAnchors['KEYS & CHESTS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'KEYS & CHESTS');
    y += 4;
    {
      const keysIntro = this.add.text(contentLeft, y,
        `From level ${KEY_CHEST_MIN_LEVEL}, keys and chests appear in the maze. ` +
        `Collect keys and press O while facing a chest to open it. ` +
        `You can carry up to ${MAX_PLAYER_KEYS} keys at a time. Keys persist across levels.`,
        BODY_STYLE);
      this._scrollContainer.add(keysIntro);
      y += keysIntro.height + 10;
    }

    y = this._addIconRow(contentLeft, y, TEX_KEY_METALLIC, 'Metallic Key',
      'The most common key. Opens metallic chests only.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_KEY_COPPER, 'Copper Key',
      'A mid-tier key. Opens copper and metallic chests.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_KEY_GOLD, 'Gold Key',
      'The rarest key. Opens any chest.');
    y += 8;

    y = this._addIconRow(contentLeft, y, TEX_CHEST_METALLIC, 'Metallic Chest',
      'Contains common loot: a bag of coins or nothing.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_CHEST_COPPER, 'Copper Chest',
      'Contains better loot: a bag of coins or a contained heart.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_CHEST_GOLD, 'Gold Chest',
      'Contains the best loot: a bag of coins, hearts, spirit stars, or even Agmuth.');
    y += 8;

    y = this._addSectionTitle(contentLeft, y, 'CHEST LOOT');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_LOOT_COIN_BAG, 'Bag of Coins',
      `A heavy pouch of ${CHEST_COIN_BAG_AMOUNT} coins. Walk over it to collect them all at once.`);
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_LOOT_HEART, 'Contained Heart',
      'Restores one lost life. Does not appear if you already have max lives.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_LOOT_STAR, 'Spirit Star',
      'Fully restores your health to 100%. A rare and powerful find.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_LOOT_AGMUTH, 'Agmuth',
      `An ancient relic of immense power. Extremely rare. Up to ${AGMUTH_MAX_PER_GAME} can be found per journey. ` +
      'Press A to activate and pass through one wall. Use wisely!');
    y += 12;

    // ============================================================
    // THE MYSTIC COMPASS
    // ============================================================
    this._sectionAnchors['THE MYSTIC COMPASS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'THE MYSTIC COMPASS');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_COMPASS, 'Compass',
      `An ancient navigational relic, bound to the shifting geometry of the ` +
      `labyrinth. Each compass manifests with a ${Math.round(COMPASS_SPAWN_CHANCE * 100)}% ` +
      `probability when you enter a new level. Walk over it to collect.`);
    y += 4;
    {
      const compassLore = this.add.text(contentLeft, y,
        'Once collected, a compass dial appears at the bottom-right of the ' +
        'screen, its needle forever trembling toward the exit. But the compass ' +
        'is a fleeting gift — forged by the ancient navigators who were themselves ' +
        'lost in these halls, each compass is bound to a single maze. When you ' +
        'reach the exit, the compass crumbles into luminous dust, its purpose ' +
        'fulfilled. A new one must be found for the next labyrinth.',
        BODY_STYLE);
      this._scrollContainer.add(compassLore);
      y += compassLore.height + 12;
    }

    // ============================================================
    // MANUSCRIPTS
    // ============================================================
    this._sectionAnchors['MANUSCRIPTS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'MANUSCRIPTS');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_MANUSCRIPT, 'Manuscript',
      `Ancient parchments from the cult of Threia, scattered throughout the ` +
      `labyrinth. Each level has a ${Math.round(MANUSCRIPT_SPAWN_CHANCE * 100)}% chance ` +
      `to contain one. Walk over it to collect.`);
    y += 4;
    {
      const manuscriptLore = this.add.text(contentLeft, y,
        'There are 30 manuscripts in total, each telling a fragment of the ' +
        'history of Goddess Threia, the dark god Vorath, and the Order of ' +
        'Threia. Once collected, a manuscript is permanently saved and can be ' +
        'read from the MANUSCRIPTS menu on the title screen.\n\n' +
        'Only manuscripts you have not yet found will appear in the maze. ' +
        'Seek them all to uncover the full lore of Limbo.',
        BODY_STYLE);
      this._scrollContainer.add(manuscriptLore);
      y += manuscriptLore.height + 12;
    }
    y = this._addSeparator(y);

    // ============================================================
    // BATS
    // ============================================================
    this._sectionAnchors['BATS'] = y;
    y = this._addSectionTitle(contentLeft, y, 'BATS');
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_BAT_WINGS_UP, 'Bat',
      `From level ${BAT_MIN_LEVEL}+, shadowy bats may appear at the edge of your ` +
      `vision as you explore new areas. They fly erratically over the maze walls, ` +
      `screeching as they pass.`);
    y += 4;
    {
      const batBody = this.add.text(contentLeft, y,
        'Bats are ambient creatures — they will not attack you on their own. ' +
        'However, if you pick up any item (food, coins, keys, compass, totem, ' +
        'or chest loot) while a bat is nearby, the nearest bat will dive toward ' +
        `you for an attack, dealing ${BAT_ATTACK_DAMAGE_MIN}-${BAT_ATTACK_DAMAGE_MAX} HP damage.\n\n` +
        "If you hold a Threia Totem when a bat attacks, a blinding flash of " +
        'light will emanate from you, scaring the bat away without taking damage. ' +
        'This protection does not consume the totem.',
        BODY_STYLE);
      this._scrollContainer.add(batBody);
      y += batBody.height + 12;
    }

    // ============================================================
    // AGMUTH (GHOST MODE)
    // ============================================================
    this._sectionAnchors['AGMUTH — GHOST MODE'] = y;
    y = this._addSectionTitle(contentLeft, y, 'AGMUTH — GHOST MODE');
    y += 4;
    {
      const agmuthLore = this.add.text(contentLeft, y,
        'The Agmuth is a fragment of primordial energy, older than the labyrinth ' +
        'itself. When activated, the soul becomes ethereal — a golden phantom that ' +
        'can phase through a single wall.\n\n' +
        'Press A to enter ghost mode. The soul glows gold and becomes translucent. ' +
        'Move into a wall to pass through it. After crossing one wall, the Agmuth ' +
        'is consumed and ghost mode ends.\n\n' +
        `Up to ${AGMUTH_MAX_PER_GAME} Agmuths can be found in gold chests throughout your journey. ` +
        'They are exceedingly rare — conserve them for when you truly need an escape.\n\n' +
        'Legend says that if you gather all four Agmuths, something special happens...',
        BODY_STYLE);
      this._scrollContainer.add(agmuthLore);
      y += agmuthLore.height + 12;
    }
    y = this._addSeparator(y);

    // ============================================================
    // THREIA'S BLESSING
    // ============================================================
    this._sectionAnchors["THREIA'S BLESSING"] = y;
    y = this._addSectionTitle(contentLeft, y, "THREIA'S BLESSING");
    y += 4;
    {
      const threiaLore = this.add.text(contentLeft, y,
        'Threia, Goddess of Lost Souls, watches over all who wander the labyrinth. ' +
        'She is the ancient caretaker of Limbo, and her gaze falls upon those who ' +
        'seek her aid with unwavering devotion.\n\n' +
        'If you stand still and press H repeatedly with fervent prayer, you may ' +
        'feel her presence. Your soul will begin to glow as her attention turns ' +
        'toward you. Continue praying, and if she judges your soul worthy, ' +
        'a blinding light will fill the maze — her divine blessing.\n\n' +
        'Those who wander the corridors may sometimes find a mysterious totem ' +
        'bearing her mark. It is said that carrying such a relic deepens ' +
        'the connection to the Goddess...\n\n' +
        'Threia answers only once per maze. Choose the moment wisely.',
        BODY_STYLE);
      this._scrollContainer.add(threiaLore);
      y += threiaLore.height + 10;
    }
    y = this._addIconRow(contentLeft, y, TEX_THREIA_TOTEM, "Threia's Totem",
      `A sacred obelisk bearing the all-seeing eye of the Goddess. ` +
      `It's a rare and very valuable item, but sometimes you'll be lucky ` +
      `enough to find it in the level. Carrying it deepens the bond with ` +
      `Threia, greatly increasing the chance of receiving her blessing. ` +
      `The totem is consumed upon use.`);
    y += 12;

    // ============================================================
    // HAMMER OF THREIA
    // ============================================================
    this._sectionAnchors['HAMMER OF THREIA'] = y;
    y = this._addSectionTitle(contentLeft, y, 'HAMMER OF THREIA');
    y += 4;
    {
      const hammerLore = this.add.text(contentLeft, y,
        'Among the relics of Threia, none is more feared by the labyrinth itself ' +
        'than the Hammer. Legends say the Goddess forged it from the shattered ' +
        'remains of the first maze — the prison that once held her before she ' +
        'broke free and ascended to divinity.\n\n' +
        'The Hammer carries within it a fragment of her wrath. When struck against ' +
        'the walls of the labyrinth, they crack and crumble as if remembering the ' +
        'Goddess who once shattered her own prison.\n\n' +
        'Some hammers are found hidden within the corridors, placed there by Threia ' +
        'herself for those brave enough to seek them. Others can be acquired from ' +
        'merchants who trade in divine artefacts. The walls, however, are not without ' +
        'memory — given time, they will mend themselves, for the labyrinth does not ' +
        'forgive those who defy its structure.',
        BODY_STYLE);
      this._scrollContainer.add(hammerLore);
      y += hammerLore.height + 10;
    }
    y = this._addIconRow(contentLeft, y, TEX_HAMMER_OF_THREIA, 'Hammer of Threia',
      'A divine hammer forged by the Goddess from the bones of the first maze. ' +
      'Face a wall and strike it repeatedly with B to shatter it. ' +
      'The labyrinth heals its wounds — broken walls regenerate after a time. ' +
      'The boundary walls that contain the maze cannot be broken.');
    y += 12;

    // ============================================================
    // VORATH'S VAULT
    // ============================================================
    this._sectionAnchors["VORATH'S VAULT"] = y;
    y = this._addSectionTitle(contentLeft, y, "VORATH'S VAULT");
    y += 4;
    y = this._addIconRow(contentLeft, y, TEX_GOLDEN_EXIT, 'Golden Exit Portal',
      `Every ${TREASURE_ROOM_LEVEL_INTERVAL} levels (starting at level ${TREASURE_ROOM_LEVEL_INTERVAL}), ` +
      `a golden exit portal has a ${Math.round(TREASURE_ROOM_SPAWN_CHANCE * 100)}% chance of appearing ` +
      'somewhere in the maze alongside the normal exit.');
    y += 4;
    y = this._addSection(contentLeft, y,
      "VORATH'S HOARD",
      'Stepping on the golden portal transports you into Vorath\'s vault — ' +
      `a small maze filled with ${TREASURE_ROOM_COIN_COUNT} coins and ` +
      `${TREASURE_ROOM_COIN_BAG_COUNT} coin bags (${CHEST_COIN_BAG_AMOUNT} coins each). ` +
      `You have ${TREASURE_ROOM_TIMER_SECONDS} seconds to collect as many as you can. ` +
      'You are fully invulnerable inside the vault — no health drain, no hazards, no bats. ' +
      'When time runs out, Vorath expels you back to the maze at the golden portal\'s position.');
    y += 4;
    y = this._addSection(contentLeft, y,
      'SPENT PORTAL',
      'After returning from the vault, the golden portal becomes spent (darkened) ' +
      'and cannot be entered again for the rest of the run. A new game resets it.');
    y += 12;
    y = this._addSeparator(y);

    // ============================================================
    // THE SHOP
    // ============================================================
    this._sectionAnchors['THE SHOP'] = y;
    y = this._addSectionTitle(contentLeft, y, 'THE SHOP');
    y += 4;
    {
      const shopIntro = this.add.text(contentLeft, y,
        'Coins collected during your journey accumulate into a persistent ' +
        'wallet that survives across all playthroughs. Spend them in the Shop ' +
        '(accessible from the title screen) to buy items that provide powerful ' +
        'benefits during gameplay.\n\n' +
        'Items fall into three categories:\n' +
        '  CONSUMABLE - Lost on game over. Can be repurchased each run.\n' +
        '  PERMANENT  - Kept forever once bought. Cannot be repurchased.\n' +
        '  DURABILITY - Has limited charges. Kept until charges run out,\n' +
        '               then shatters and can be repurchased.\n\n' +
        'Durability items show a charge arc in the HUD. The arc shrinks as ' +
        'charges deplete and changes color: white > yellow > red.',
        BODY_STYLE);
      this._scrollContainer.add(shopIntro);
      y += shopIntro.height + 10;
    }

    y = this._addIconRow(contentLeft, y, TEX_SHOP_AGMUTH_PENDANT, `Agmuth Pendant (${SHOP_PRICE_AGMUTH_PENDANT} coins)`,
      `[Permanent] Increases probability of Agmuth drops from copper and gold chests by ` +
      `+${Math.round(AGMUTH_PENDANT_BONUS * 100)}%. Kept forever once purchased.`);
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_ELEMENTAL_RING, `Elemental Ring (${SHOP_PRICE_ELEMENTAL_RING} coins)`,
      `[Durability: ${DURABILITY_ELEMENTAL_RING} charges] ` +
      'Grants full immunity to lava, water, and poison floors from a shared charge pool. ' +
      'While active, individual element rings are hidden and preserved. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_HAMMER_OF_THREIA, `Hammer of Threia (${SHOP_PRICE_HAMMER_OF_THREIA} coins)`,
      `[Durability: ${DURABILITY_HAMMER_OF_THREIA} charges] ` +
      `A divine hammer forged by Threia. Hold a direction toward a wall and press B ` +
      `${HAMMER_BREAK_PRESSES} times rapidly to shatter it. Can also be found sometimes ` +
      'while exploring the maze. Charges stack from both sources. ' +
      'Walls regenerate after 45 seconds. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_HEART_PENDANT, `Heart Pendant (${SHOP_PRICE_HEART_PENDANT} coins)`,
      `[Durability: ${DURABILITY_HEART_PENDANT} charges] ` +
      `Reduces energy drain per step by ${Math.round(HEART_PENDANT_DRAIN_REDUCTION * 100)}%. ` +
      'Applies to normal and hazard drain. 1 charge per protected step. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_HEART_POTION, `Heart Potion (${SHOP_PRICE_HEART_POTION} coins)`,
      '[Consumable] When you lose a life, a potion auto-consumes: restores the lost heart ' +
      'and fills energy to full. Buy up to 3. Lost on game over.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_KEY_PENDANT, `Key Pendant (${SHOP_PRICE_KEY_PENDANT} coins)`,
      `[Permanent] Increases key spawn frequency by +${Math.round(KEY_PENDANT_SPAWN_BONUS * 100)}%. ` +
      'Kept forever once purchased.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_KEY_COPPER, `Copper Key (${SHOP_PRICE_KEY_COPPER} coins)`,
      '[Consumable] A copper key, delivered to your inventory at game start. ' +
      'Opens metallic and copper chests. Lost on game over.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_KEY_GOLD, `Gold Key (${SHOP_PRICE_KEY_GOLD} coins)`,
      '[Consumable] A golden key, delivered to your inventory at game start. ' +
      'Opens any chest. Lost on game over.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_KEY_METALLIC, `Metallic Key (${SHOP_PRICE_KEY_METALLIC} coins)`,
      '[Consumable] A metallic key, delivered to your inventory at game start. ' +
      'Opens metallic chests only. Lost on game over.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_LAVA_RING, `Lava Ring (${SHOP_PRICE_LAVA_RING} coins)`,
      `[Durability: ${DURABILITY_LAVA_RING} charges] ` +
      'Grants full immunity to lava floor damage. 1 charge per protected step or idle tick. ' +
      'Superseded while Elemental Ring is active. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_POISON_RING, `Poison Ring (${SHOP_PRICE_POISON_RING} coins)`,
      `[Durability: ${DURABILITY_POISON_RING} charges] ` +
      'Grants full immunity to poison floor effects. 1 charge per protected step. ' +
      'Superseded while Elemental Ring is active. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_TEAR_OF_THREIA, `Tear of Threia (${SHOP_PRICE_TEAR_OF_THREIA} coins)`,
      '[Consumable] A crystallised tear shed by the goddess Threia herself. ' +
      'When all lives are lost, the Tear shatters and Threia\'s grace descends — ' +
      'every heart is restored and energy refilled. One use. Lost on game over.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_THREIAS_GUIDE, `Threia's Guide (${SHOP_PRICE_THREIAS_GUIDE} coins)`,
      `[Durability: ${DURABILITY_THREIAS_GUIDE} charges] A sacred scroll. Each level has a ` +
      `${Math.round(THREIAS_GUIDE_REVEAL_CHANCE * 100)}% chance to reveal the entire maze, ` +
      'removing fog of war completely. 1 charge per successful reveal. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_TOTEM_VORACIOUS, `Totem of the Voracious (${SHOP_PRICE_TOTEM_VORACIOUS} coins)`,
      `[Durability: ${DURABILITY_TOTEM_VORACIOUS} charges] ` +
      `All food within ${TOTEM_VORACIOUS_ATTRACT_RADIUS} tiles is magnetically pulled toward you. ` +
      '1 charge per food item attracted. Max 1.');
    y += 2;
    y = this._addIconRow(contentLeft, y, TEX_SHOP_WATER_RING, `Water Ring (${SHOP_PRICE_WATER_RING} coins)`,
      `[Durability: ${DURABILITY_WATER_RING} charges] ` +
      'Grants full immunity to water floor damage. 1 charge per protected step or idle tick. ' +
      'Superseded while Elemental Ring is active. Max 1.');
    y += 20;
    y = this._addSeparator(y);

    // ============================================================
    // LEVEL EVENTS
    // ============================================================
    this._sectionAnchors['LEVEL EVENTS'] = y;
    y = this._addSection(contentLeft, y, 'LEVEL EVENTS',
      'From level 3 onwards, each level has a 20% chance to\n' +
      'be affected by a random event modifier. Only one event\n' +
      'can be active per level. The event name is announced\n' +
      'at level start and shown as a golden indicator in the HUD.');
    y += 2;
    y = this._addSubItem(contentLeft, y, "VORATH'S HUNGER",
      'Food disappears twice as fast. Vorath devours sustenance from the labyrinth.');
    y += 2;
    y = this._addSubItem(contentLeft, y, "THREIA'S LIGHT",
      'Fog reveal radius increased by +2 tiles. Threia illuminates the path ahead.');
    y += 2;
    y = this._addSubItem(contentLeft, y, 'THE SILENT MAZE',
      'No bats spawn, no narrator speaks. An eerie silence fills the corridors.');
    y += 2;
    y = this._addSubItem(contentLeft, y, 'GOLD RUSH',
      '3x coin spawns, but no food appears at all. Gold floods the corridors at the cost of sustenance.');
    y += 2;
    y = this._addSubItem(contentLeft, y, 'THE LABYRINTH REMEMBERS',
      'Visited fog gradually re-darkens over time. The maze forgets your steps — keep moving!');
    y += 20;

    // ============================================================
    // SECRET PASSAGES
    // ============================================================
    this._sectionAnchors['SECRET PASSAGES'] = y;
    y = this._addSection(contentLeft, y, 'SECRET PASSAGES',
      'From level 3 onwards, some walls hide secret passages.\n' +
      'Look for subtle visual cues on certain walls. Push\n' +
      'against a suspicious wall 3 times quickly to reveal\n' +
      'a hidden shortcut. Once opened, passages stay open\n' +
      'permanently for the rest of the level.');
    y += 20;

    // ============================================================
    // MOVING WALLS
    // ============================================================
    this._sectionAnchors['MOVING WALLS'] = y;
    y = this._addSection(contentLeft, y, 'MOVING WALLS',
      'From level 8 onwards, some walls in the maze\n' +
      'periodically change: walls open and close on a timer,\n' +
      'sections rotate, or walls vanish and reappear. Watch\n' +
      'for movement and plan your route. If you are standing\n' +
      'where a wall would appear, the change is delayed.');
    y += 20;

    // ============================================================
    // SHADOW WALKERS
    // ============================================================
    this._sectionAnchors['SHADOW WALKERS'] = y;
    y = this._addSection(contentLeft, y, 'SHADOW WALKERS',
      'From level 7 onwards, ghostly shadows patrol the\n' +
      'corridors. They wander randomly but chase you on\n' +
      'sight (5-tile range), dealing 15 HP damage and\n' +
      'stealing 3 coins on contact. Unlike other creatures,\n' +
      'Shadow Walkers persist — they retreat after hitting\n' +
      'you, then return to their patrol. They are invisible\n' +
      'in fog. Using Agmuth ghost mode lets you pass\n' +
      'through them harmlessly.');
    y += 20;

    // ============================================================
    // BAT NESTS
    // ============================================================
    this._sectionAnchors['BAT NESTS'] = y;
    y = this._addSection(contentLeft, y, 'BAT NESTS',
      'From level 4 onwards, bat nests may appear in the maze.\n' +
      'Nests sit in breached walls — look for crumbled wall\n' +
      'remnants and tiny fluttering bats as visual cues.\n' +
      'You CAN walk through a nest, but it costs double damage\n' +
      'and double coin loss. Staying on a nest keeps hurting!\n' +
      'Getting within 3 tiles triggers a swarm of bats. Nests\n' +
      'reactivate after a 12-second cooldown. Invisible in fog.\n' +
      'Use the Hammer of Threia (3 hits) to destroy a nest\n' +
      'permanently.');
    y += 20;
    y = this._addSeparator(y);

    // ============================================================
    // MINI-MAP
    // ============================================================
    this._sectionAnchors['MINI-MAP'] = y;
    y = this._addSection(contentLeft, y, 'MINI-MAP',
      'Press TAB to toggle a small overview map in the bottom-right\n' +
      'corner of the screen. The mini-map shows explored areas at a\n' +
      'reduced scale: walls, paths, hazards (water, lava, poison,\n' +
      'spikes), your current position, and the exit location if\n' +
      'you have collected the Mystic Compass. Unexplored areas\n' +
      'remain hidden. The mini-map stays on screen until you\n' +
      'toggle it off again with TAB.');
    y += 8;
    y = this._addSeparator(y);

    // ============================================================
    // DIRECTION TRAIL
    // ============================================================
    this._sectionAnchors['DIRECTION TRAIL'] = y;
    y = this._addSection(contentLeft, y, 'DIRECTION TRAIL',
      'As you move through the maze, subtle dots mark the last\n' +
      'few cells you visited. The trail helps you remember which\n' +
      'direction you came from, especially in large mazes where\n' +
      'corridors look alike. Newer dots are brighter; older ones\n' +
      'fade away. The trail is always visible — no item needed.');
    y += 20;
    y = this._addSeparator(y);

    // ============================================================
    // TIPS
    // ============================================================
    this._sectionAnchors['TIPS'] = y;
    y = this._addSection(contentLeft, y, 'TIPS',
      '- Collect food often; don\'t let health drop too low.\n' +
      '- Coins are valuable: 10 coins = 1 extra life.\n' +
      '- Keys appear from level 3 — save gold keys for gold chests!\n' +
      '- Face a chest and press O to open it with a matching key.\n' +
      '- Water and lava patches are long — plan your route.\n' +
      '- Poison slows and drains you — avoid green tiles!\n' +
      '- Spikes wound you for 10 seconds — watch for the metallic tiles!\n' +
      '- Resting heals, but the score cost adds up fast.\n' +
      '- The mystic compass points to the exit — seek it out!\n' +
      '- Agmuths are ultra-rare — save them for desperate moments!\n' +
      '- Press A to activate an Agmuth and phase through one wall.\n' +
      '- Push against suspicious-looking walls — they may be secrets!\n' +
      '- Watch for moving walls — corridors can change around you.\n' +
      '- Avoid Shadow Walkers or use ghost mode to slip past them.\n' +
      '- Destroy bat nests with the hammer before they swarm you.\n' +
      '- Press TAB to open the mini-map and plan your route.\n' +
      '- The direction trail shows where you just came from.\n' +
      '- Complete levels quickly for a time bonus!');
    y += 20;

    // ============================================================
    // FOOTER / PROMPT
    // ============================================================
    const prompt = this.add.text(cx, y, 'Press ESC or ENTER to return', HINT_STYLE)
      .setOrigin(0.5);
    this._scrollContainer.add(prompt);

    // Blinking prompt
    this.tweens.add({
      targets: prompt,
      alpha: 0.3,
      duration: 700,
      yoyo: true,
      repeat: -1,
    });
    y += 30;

    // Total content height for scroll clamping
    this._contentHeight = y;
    this._maxScroll = Math.max(0, this._contentHeight - (GAME_HEIGHT - 20));

    // Scroll hint (only shown if content overflows)
    if (this._maxScroll > 0) {
      this._scrollHint = this.add.text(
        GAME_WIDTH - BORDER_MARGIN - 10,
        GAME_HEIGHT - BORDER_MARGIN - 10,
        'UP/DOWN to scroll',
        { ...HINT_STYLE, fontSize: '10px' },
      ).setOrigin(1, 1);
      // Not in the scroll container — stays fixed
    }

    // "Back to Contents" hint — fixed position just above the scroll hint
    this._backToTop = this.add.text(
      GAME_WIDTH - BORDER_MARGIN - 10,
      GAME_HEIGHT - BORDER_MARGIN - 22,
      '\u25B2  CONTENTS',
      { ...HINT_STYLE, fontSize: '10px', color: '#666666' },
    ).setOrigin(1, 1).setAlpha(0);
    this._backToTop.setInteractive({ useHandCursor: true });
    this._backToTop.on('pointerover', () => this._backToTop.setColor('#ffffff'));
    this._backToTop.on('pointerout', () => this._backToTop.setColor('#666666'));
    this._backToTop.on('pointerdown', () => {
      try { this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.3) }); } catch (_e) { /* */ }
      this._scrollToSection('_TOP');
    });
    // Register a virtual anchor at the very top
    this._sectionAnchors['_TOP'] = 0;

    // ============================================================
    // INPUT
    // ============================================================
    this._setupInput();
  }

  // ---------------------------------------------------------------
  // Content Builders
  // ---------------------------------------------------------------

  /**
   * Add a section with a title and body text. Returns the new Y position.
   * @private
   */
  _addSection(x, y, title, body) {
    y = this._addSectionTitle(x, y, title);
    y += 4;

    const bodyText = this.add.text(x, y, body, BODY_STYLE);
    this._scrollContainer.add(bodyText);
    y += bodyText.height + 12;

    return y;
  }

  /**
   * Add a sub-item (indented bullet) under a parent section.
   * Visually distinct from top-level sections: uses a bullet marker
   * and indented layout so items appear nested.
   * @private
   */
  _addSubItem(x, y, name, description) {
    const indent = 20;
    const nameText = this.add.text(x + indent, y, `  ${name}`, {
      ...ITEM_LABEL_STYLE,
      fontSize: '12px',
    });
    this._scrollContainer.add(nameText);
    y += nameText.height + 2;

    const descText = this.add.text(x + indent + 16, y, description, {
      ...ITEM_DESC_STYLE,
      wordWrap: { width: 410 },
    });
    this._scrollContainer.add(descText);
    y += descText.height + 6;

    return y;
  }

  /**
   * Add a horizontal separator line between major sections.
   * @private
   */
  _addSeparator(y) {
    const cx = GAME_WIDTH / 2;
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.12);
    g.lineBetween(cx - 180, y, cx + 180, y);
    this._scrollContainer.add(g);
    return y + 8;
  }

  /**
   * Add a section title. Returns the new Y position.
   * @private
   */
  _addSectionTitle(x, y, title) {
    // Decorative dash before title
    const titleText = this.add.text(x, y, `-- ${title} --`, SECTION_TITLE_STYLE);
    this._scrollContainer.add(titleText);
    y += titleText.height + 2;
    return y;
  }

  /**
   * Add a row with a texture icon, label, and description.
   * @private
   * @param {number} x
   * @param {number} y
   * @param {string} textureKey - Phaser texture key.
   * @param {string} label
   * @param {string} description
   * @param {number} [iconScale=1.8] - Scale factor for the icon.
   * @param {number} [iconAngle=0] - Rotation angle in degrees.
   * @returns {number} New Y position.
   */
  _addIconRow(x, y, textureKey, label, description, iconScale = 1.8, iconAngle = 0) {
    const iconX = x + 12;
    const iconY = y + 10;

    // Icon background circle (subtle dark circle behind the icon)
    const bg = this.add.circle(iconX, iconY, 14, COLOR_WHITE, 0.07);
    this._scrollContainer.add(bg);

    // Icon sprite
    const icon = this.add.image(iconX, iconY, textureKey).setScale(iconScale);
    if (iconAngle) icon.setAngle(iconAngle);
    this._scrollContainer.add(icon);

    // Label (to the right of the icon)
    const labelText = this.add.text(iconX + 24, y, label, ITEM_LABEL_STYLE);
    this._scrollContainer.add(labelText);

    // Description (below the label, indented)
    const descText = this.add.text(iconX + 24, y + 18, description, ITEM_DESC_STYLE);
    this._scrollContainer.add(descText);

    const rowHeight = Math.max(30, 18 + descText.height + 4);
    return y + rowHeight;
  }

  /**
   * Add a floor-type row with a tile swatch, label, and description.
   * @private
   * @param {number} x
   * @param {number} y
   * @param {string|null} textureKey - Texture key, or null for plain floor.
   * @param {string} label
   * @param {string} description
   * @returns {number} New Y position.
   */
  _addFloorRow(x, y, textureKey, label, description, tint) {
    const swatchX = x + 12;
    const swatchY = y + 10;
    const swatchSize = TILE_SIZE;

    if (textureKey) {
      // Use the actual floor texture as a swatch
      const swatch = this.add.image(swatchX, swatchY, textureKey);
      swatch.setDisplaySize(swatchSize, swatchSize);
      this._scrollContainer.add(swatch);
    } else {
      // Plain floor: draw a dark gray square
      const g = this.add.graphics();
      g.fillStyle(tint || 0x333333, 1);
      g.fillRect(swatchX - swatchSize / 2, swatchY - swatchSize / 2, swatchSize, swatchSize);
      g.lineStyle(1, COLOR_WHITE, 0.15);
      g.strokeRect(swatchX - swatchSize / 2, swatchY - swatchSize / 2, swatchSize, swatchSize);
      this._scrollContainer.add(g);
    }

    // Label
    const labelText = this.add.text(swatchX + 22, y, label, ITEM_LABEL_STYLE);
    this._scrollContainer.add(labelText);

    // Description
    const descText = this.add.text(swatchX + 22, y + 18, description, ITEM_DESC_STYLE);
    this._scrollContainer.add(descText);

    const rowHeight = Math.max(30, 18 + descText.height + 4);
    return y + rowHeight;
  }

  // ---------------------------------------------------------------
  // Input & Scrolling
  // ---------------------------------------------------------------

  /** @private */
  _setupInput() {
    // Return to title
    this.input.keyboard.on('keydown-ESC', () => {
      this.sound.play(AUDIO_ESC_PRESSED, { volume: getSfxVol(0.5) });
      this._returnToTitle();
    });
    this.input.keyboard.on('keydown-ENTER', () => {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
      this._returnToTitle();
    });
    this.input.keyboard.on('keydown-SPACE', () => {
      this.sound.play(AUDIO_MENU_CLICK, { volume: getSfxVol(0.5) });
      this._returnToTitle();
    });

    // Scroll
    this.input.keyboard.on('keydown-UP', () => this._scroll(-40));
    this.input.keyboard.on('keydown-DOWN', () => this._scroll(40));

    // Mouse wheel scroll
    this.input.on('wheel', (_pointer, _gos, _dx, dy) => {
      this._scroll(dy > 0 ? 40 : -40);
    });
  }

  /**
   * Scroll the content container by a delta.
   * @private
   * @param {number} delta - Pixels to scroll (positive = down, negative = up).
   */
  _scroll(delta) {
    this._scrollY = Phaser.Math.Clamp(
      this._scrollY + delta,
      0,
      this._maxScroll,
    );
    this._scrollContainer.setY(-this._scrollY);
    this._updateScrollIndicators();
  }

  /**
   * Update fixed scroll indicators (scroll hint + back-to-top) based on
   * the current `_scrollY` position.
   * @private
   */
  _updateScrollIndicators() {
    // Scroll-down hint
    if (this._scrollHint && this._scrollY >= this._maxScroll) {
      this._scrollHint.setAlpha(0.2);
    } else if (this._scrollHint) {
      this._scrollHint.setAlpha(1);
    }

    // Back-to-Contents hint — visible once scrolled past the TOC area
    if (this._backToTop) {
      const show = this._scrollY > this._tocEndY;
      this._backToTop.setAlpha(show ? 1 : 0);
      this._backToTop.setVisible(show);
    }
  }

  /**
   * Smoothly scroll to a named section anchor.
   * @private
   * @param {string} anchorKey - Key in `_sectionAnchors` to scroll to.
   */
  _scrollToSection(anchorKey) {
    const targetY = this._sectionAnchors[anchorKey];
    if (targetY === undefined) return;

    // Place the section title at the top of the visible area (below the mask)
    const maskPad = BORDER_MARGIN + 8;
    const dest = Phaser.Math.Clamp(targetY - maskPad - 4, 0, this._maxScroll);

    // Kill any existing scroll tween to avoid conflicts
    if (this._scrollTween) {
      this._scrollTween.stop();
      this._scrollTween = null;
    }

    // Use a proxy object for the tween since Phaser tweens work most reliably
    // with plain numeric properties on a simple target.
    const proxy = { value: this._scrollY };
    const self = this;

    this._scrollTween = this.tweens.add({
      targets: proxy,
      value: dest,
      duration: 400,
      ease: 'Cubic.easeOut',
      onUpdate() {
        self._scrollY = proxy.value;
        self._scrollContainer.setY(-self._scrollY);
        self._updateScrollIndicators();
      },
      onComplete() {
        self._scrollY = dest;
        self._scrollContainer.setY(-dest);
        self._scrollTween = null;
        self._updateScrollIndicators();
      },
    });
  }

  /** @private */
  _returnToTitle() {
    if (this._transitioning) return;
    this._transitioning = true;

    this.cameras.main.fadeOut(400, 0, 0, 0);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start(SCENE_TITLE);
    });
  }

  // ---------------------------------------------------------------
  // Decorative Border
  // ---------------------------------------------------------------

  /** @private Draw the decorative border matching all other scenes. */
  _drawBorder() {
    const g = this.add.graphics();
    g.lineStyle(1, COLOR_WHITE, 0.2);
    g.strokeRect(BORDER_MARGIN, BORDER_MARGIN,
      GAME_WIDTH - BORDER_MARGIN * 2, GAME_HEIGHT - BORDER_MARGIN * 2);

    const cornerSize = 10;
    const m = BORDER_MARGIN + 5;
    g.lineStyle(1, COLOR_WHITE, 0.4);

    // Top-left
    g.lineBetween(m, m, m + cornerSize, m);
    g.lineBetween(m, m, m, m + cornerSize);
    // Top-right
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m - cornerSize, m);
    g.lineBetween(GAME_WIDTH - m, m, GAME_WIDTH - m, m + cornerSize);
    // Bottom-left
    g.lineBetween(m, GAME_HEIGHT - m, m + cornerSize, GAME_HEIGHT - m);
    g.lineBetween(m, GAME_HEIGHT - m, m, GAME_HEIGHT - m - cornerSize);
    // Bottom-right
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m - cornerSize, GAME_HEIGHT - m);
    g.lineBetween(GAME_WIDTH - m, GAME_HEIGHT - m, GAME_WIDTH - m, GAME_HEIGHT - m - cornerSize);
  }
}
